import React, { useCallback } from "react";
import classNames from "classnames";
import { FLAT_RATE_PLANS, flatRatePlanNames } from "@common/flatRateBilling";
import { useBillingStatus } from "@hooks/useBillingStatus";
import { useRecommendedPlan } from "@hooks/useRecommendedPlan";
import { Button } from "@design-system/Button/Button";
import { useConstant } from "@hooks/useConstant";
import { Plans } from "../../billing/Plans/Plans";
import { usePlanManagement } from "../../billing/PlanManagement/PlanManagementContext";
import animations from "../layout/OnboardingLayout.module.scss";
import { Footer } from "../components/Footer";
import commonStyles from "./common.module.scss";

interface Props {
    onContinue: () => void;
    back: () => void;
    onEnterpriseCall: () => void;
}

export const ChoosePlan: React.FC<Props> = ({
    onContinue,
    back,
    onEnterpriseCall,
}) => {
    const { upgrade } = usePlanManagement();
    const { plans } = useBillingStatus();
    const recommendedPlan = useRecommendedPlan();
    const isBeforeOfferDeadline = useConstant(
        () => new Date() < new Date("2026-03-01"),
    );

    const renderAction = useCallback(
        (plan: FLAT_RATE_PLANS) => {
            if (plan === FLAT_RATE_PLANS.FREE) {
                return (
                    <Button variant="primary" size="lg" onClick={onContinue}>
                        Continue on Free
                    </Button>
                );
            }

            if (plan === FLAT_RATE_PLANS.ENTERPRISE) {
                return (
                    <Button
                        variant="tertiary"
                        size="lg"
                        onClick={() => onEnterpriseCall()}
                        data-testid={`upgrade-${plan.toLowerCase()}`}
                    >
                        Book a call
                    </Button>
                );
            }

            if (plan === FLAT_RATE_PLANS.FRESHBOOKS) {
                return (
                    <Button
                        variant="primary"
                        size="lg"
                        onClick={() => onEnterpriseCall()}
                        data-testid={`upgrade-${plan.toLowerCase()}`}
                    >
                        Continue on FreshBooks
                    </Button>
                );
            }

            return (
                <Button
                    variant="tertiary"
                    size="lg"
                    onClick={() => upgrade(plan, true)}
                    data-testid={`upgrade-${plan.toLowerCase()}`}
                >
                    Start Free
                </Button>
            );
        },
        [onContinue, onEnterpriseCall, upgrade],
    );

    const isFreshbooksPlanAvailable = plans.some(
        (plan) => plan.plan === FLAT_RATE_PLANS.FRESHBOOKS,
    );
    const displayOffer = isBeforeOfferDeadline && isFreshbooksPlanAvailable;

    return (
        <div
            className={classNames(animations.fadeIn1, commonStyles.content1100)}
        >
            <header className={animations.fadeIn2}>
                <h1 className={commonStyles.title}>
                    We recommend the{" "}
                    <em>{flatRatePlanNames[recommendedPlan!]}</em> plan
                </h1>
                {displayOffer && (
                    <p className={commonStyles.subtitle}>
                        Sign up for 2026 bookkeeping before Mar 1, 2026 and
                        we'll catch up your entire 2025 books for free (A $2,388
                        value).
                    </p>
                )}
            </header>

            <Plans plans={plans} renderAction={renderAction} stickyHeaders />

            <Footer onBack={back} />
        </div>
    );
};
